import type { QueryRowType } from "@rocicorp/zero";
import { useState } from "react";
import { parseBranchRoutePath } from "../../../shared/oauth/resource";
import {
  spaceEdgeHostnameSuffix,
  type TenantEdgeUrlOptions,
} from "../../../shared/edgeUrl";
import { queries } from "../../../shared/queries";
import { useBranchServerReady } from "../../hooks/useBranchServerReady";
import { useInspectShortcut } from "../../hooks/useInspectShortcut";
import { useQuery } from "@rocicorp/zero/react";
import { useSessionTenantEdgeUrlOptions } from "../../hooks/useSessionTenantOrigin";
import { useSetPresence } from "../../hooks/useSetPresence";
import { useTenantCustomDomain } from "../../hooks/useTenantCustomDomain";
import { getPageUrl } from "../../lib/stepUrl";
import { BrowserAccess } from "../BrowserAccess";
import { BrowserChrome } from "../BrowserChrome";
import type { ElementSnippet } from "../Chat/chatTypes";
import { EmptyState } from "../EmptyState";
import { Page } from "../LiveBrowser/Page";
import useInspectMode from "../LiveBrowser/useInspectMode";
import useAgentControl from "../LiveBrowser/useAgentControl";
import useAgentControlBridge from "../LiveBrowser/useAgentControlBridge";
import { useNavigation } from "../LiveBrowser/useNavigation";

export type WorkflowPageLink = {
  href: string;
  origin: string;
  route: string;
  branchId?: string;
  workflowId?: string;
  externalId: string | null;
};

export type WorkflowPageInspection = {
  elementSnippets: readonly ElementSnippet[];
  onElementSelect: (element: ElementSnippet) => void;
  onElementDeselect: (sourceId: string, index: number) => void;
};

type PageBranch = QueryRowType<ReturnType<typeof queries.workflowPageForRoute>>;

export function workflowPageLink(
  href: string,
  options: TenantEdgeUrlOptions
): WorkflowPageLink | null {
  if (!URL.canParse(href)) return null;
  const url = new URL(href);
  const suffix = spaceEdgeHostnameSuffix(options);
  if (
    url.host !== new URL(options.edgeUrl).host &&
    !(suffix && url.hostname.endsWith(suffix))
  )
    return null;
  const branch = parseBranchRoutePath(url.pathname);
  return {
    href,
    origin: url.origin,
    route: branch?.path ?? url.pathname,
    ...(branch && { branchId: branch.branchId }),
    externalId: url.searchParams.get("external_id"),
  };
}

function PagePreview({
  branch,
  link,
  inspection,
}: {
  branch: PageBranch;
  link: WorkflowPageLink;
  inspection?: WorkflowPageInspection;
}) {
  const edgeUrlOptions = useSessionTenantEdgeUrlOptions();
  const customDomain = useTenantCustomDomain();
  const space = branch.workflow!.space!;
  const branchId = branch.name === "main" ? null : branch.id;
  const route = branch.headCommit?.routes[0];
  const externalId = link.externalId ?? route?.externalId ?? null;
  const navigation = useNavigation(link.route, branchId ?? undefined);
  const agentControl = useAgentControl();
  useAgentControlBridge(agentControl, route?.stepDir ?? "", true);
  useSetPresence(branch.workflowId, branchId ?? undefined);
  const responding = branch.headCommitChat?.respondingAt != null;
  const [turns, setTurns] = useState({ responding, settled: 0 });
  if (turns.responding !== responding)
    setTurns({
      responding,
      settled: turns.settled + (responding ? 0 : 1),
    });
  const ready = useBranchServerReady(branch.id);
  const src = getPageUrl(
    space.slug,
    link.route,
    edgeUrlOptions,
    { branchId, externalId },
    customDomain,
    space.customHostname
  );
  const [inspectMode, setInspectMode] = useState(false);
  const canInspect = inspection != null;
  const inspect = useInspectMode({
    enabled: canInspect && inspectMode,
    sourceId: src,
    elementSnippets: inspection?.elementSnippets.filter(
      (snippet) => snippet.sourceId === src
    ),
    onElementSelect: (element) =>
      inspection?.onElementSelect({
        ...element,
        sourceId: src,
        stepName: route!.stepDir,
        color: "sky",
      }),
    onElementDeselect: (index) => inspection?.onElementDeselect(src, index),
  });
  useInspectShortcut(() => {
    if (canInspect) setInspectMode((previous) => !previous);
  });
  return (
    <BrowserChrome
      embedded
      displayUrl={new URL(src).host + navigation.path}
      openUrl={src}
      canBack={navigation.canBack}
      canForward={navigation.canForward}
      onBack={navigation.back}
      onForward={navigation.forward}
      onReload={navigation.reload}
      inspectMode={canInspect && inspectMode}
      onInspectModeChange={canInspect ? setInspectMode : undefined}
      access={
        <BrowserAccess
          routeAuth={route?.routeAuth}
          routeGroups={route?.routeGroups}
          draft={branchId != null}
        />
      }
    >
      {ready ? (
        <Page
          reloadKey={`${turns.settled}:${navigation.reloadToken}`}
          spaceSlug={space.slug}
          spaceId={space.id}
          branchId={branchId}
          edgeRoute={link.route}
          externalId={externalId}
          features={[navigation.feature, inspect.feature, agentControl.feature]}
        />
      ) : (
        <EmptyState
          iconName="compass"
          iconSize={24}
          maxWidth={220}
          title="Loading page…"
        />
      )}
    </BrowserChrome>
  );
}

export function WorkflowPagePreview({
  link,
  inspection,
}: {
  link: WorkflowPageLink;
  inspection?: WorkflowPageInspection;
}) {
  const edgeUrlOptions = useSessionTenantEdgeUrlOptions();
  const [resolvedLink, setResolvedLink] = useState(link);
  const workflowId =
    link.workflowId ??
    (resolvedLink.href === link.href ? resolvedLink.workflowId : undefined);
  const [branches, details] = useQuery(
    queries.workflowPageForRoute({
      route: link.route,
      ...(link.branchId && { branchId: link.branchId }),
      ...(workflowId && { workflowId }),
    })
  );
  const candidates = branches.filter(
    (branch) =>
      branch.workflow?.space &&
      new URL(
        getPageUrl(branch.workflow.space.slug, link.route, edgeUrlOptions)
      ).origin === link.origin
  );
  // A live address the agent linked before publishing still previews the user’s draft.
  const target =
    candidates.find((branch) => branch.id === link.branchId) ??
    candidates.find((branch) => branch.name === "main") ??
    candidates[0];
  if (target && target.workflowId !== workflowId)
    setResolvedLink({ ...link, workflowId: target.workflowId });
  if (target)
    return (
      <PagePreview
        key={`${target.id}:${link.route}`}
        branch={target}
        link={link}
        inspection={inspection}
      />
    );
  return (
    <EmptyState
      iconName="compass"
      iconSize={24}
      maxWidth={220}
      title={details.type === "complete" ? "Page not found" : "Loading page…"}
    />
  );
}
